import React, { useEffect, useState } from "react";
import { ShieldCheck, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

/** Annual approver attestation. Blocks the portal for approvers until accepted. */
export default function AttestationGate({ children }) {
  const { user, setUser, logout } = useAuth();
  const [attestation, setAttestation] = useState(null);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_approver || !user?.attestation_due) { setAttestation(null); return; }
    api.get("/attestation")
      .then(({ data }) => setAttestation(data.required ? data : null))
      .catch(() => setAttestation(null));
  }, [user?.is_approver, user?.attestation_due]);

  const accept = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/attestation/accept");
      setUser({ ...user, attestation_due: false, attestation_accepted_at: data.accepted_at });
      setAttestation(null);
      toast.success("Attestation recorded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!attestation) return children;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: "var(--ink)" }} data-testid="attestation-gate">
      <div className="w-full max-w-2xl border p-8 md:p-10" style={{ background: "var(--surface)", borderColor: "var(--border-strong)" }}>
        <div className="eyebrow flex items-center gap-2" style={{ color: "var(--muted)" }}>
          <ShieldCheck size={14} /> Annual approver attestation
        </div>
        <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          Before you approve invoices
        </h1>
        <p className="mt-4 text-sm" style={{ color: "var(--muted)" }}>
          You are a designated approver for {user?.company}. Read and accept the attestation below to
          continue — it is recorded in the audit log and renews every 12 months.
        </p>

        <div
          className="mt-6 max-h-72 overflow-y-auto border p-5 text-[15px] leading-relaxed"
          style={{ borderColor: "var(--border)", color: "var(--ink)" }}
          data-testid="attestation-text"
        >
          {attestation.text}
        </div>

        <label className="mt-6 flex items-start gap-3 text-sm" style={{ color: "var(--ink)" }}>
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="attestation-checkbox"
          />
          <span>I have read the attestation above and I accept it.</span>
        </label>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            onClick={accept}
            disabled={!checked || saving}
            className="btn-primary flex items-center gap-2"
            data-testid="attestation-accept"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <>Accept & Continue</>}
          </button>
          <button onClick={logout} className="btn-ghost flex items-center gap-2" data-testid="attestation-signout">
            <LogOut size={14} /> Sign Out
          </button>
          <span className="text-xs" style={{ color: "var(--muted)" }}>
            Version {attestation.version}
          </span>
        </div>
      </div>
    </div>
  );
}
